/**
 * Тонкий клиент к API.
 *
 * Два правила, которые здесь зашиты:
 *   — суммы ходят строками копеек (`*Kop`), считаются в bigint и никогда
 *     не превращаются в number до момента показа;
 *   — токен лежит в localStorage и живёт 90 дней, чтобы клиент не запрашивал
 *     SMS при каждом открытии кабинета.
 */

/**
 * Ключ хранения токена зависит от того, какое приложение открыто.
 *
 * На стенде оба приложения живут на одном телефоне и одном домене: водитель
 * в /driver, кабинет клиента в /client. С общим ключом они выбивали бы друг
 * друга из сессии при каждом переключении. Раздельные ключи решают это без
 * отдельного поддомена.
 */
/*
 * Сравнение именно по границе сегмента, а не по префиксу: у оператора карточка
 * клиента живёт по адресу `/clients/<id>`, и он тоже начинается на `/client`.
 * С проверкой по префиксу такая страница читала ключ кабинета клиента, токена
 * там не находила — и оператора при каждом F5 на карточке выбрасывало на вход.
 */
const area = location.pathname === '/client' || location.pathname.startsWith('/client/') ? 'client' : 'staff';
const TOKEN_KEY = `watercrm.token.${area}`;
const SCOPE_KEY = `watercrm.scope.${area}`;

/** Область, в которую входит это приложение: подсказка серверу при входе по коду. */
export const authArea = area;

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string, scope: string) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(SCOPE_KEY, scope);
}
export function getScope(): string | null {
  return localStorage.getItem(SCOPE_KEY);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SCOPE_KEY);
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown) {
    super(message);
  }
}

export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = getToken();
  const res = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers: {
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, 'unauthorized', 'Сессия истекла, войдите заново');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? 'error', data?.message ?? `Ошибка ${res.status}`, data?.details);
  }
  return data as T;
}

// ── Деньги на экране ──────────────────────────────────────────────────────
/** «−64000» коп → «−640,00 ₽». Форматирование — единственное место, где
 *  копейки становятся рублями. */
export function money(kop: string | number | bigint | null | undefined): string {
  if (kop === null || kop === undefined) return '—';
  const v = BigInt(kop);
  const neg = v < 0n;
  const abs = neg ? -v : v;
  // Неразрывный пробел: сумма не должна переноситься по строке пополам
  const rub = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const frac = (abs % 100n).toString().padStart(2, '0');
  return `${neg ? '−' : ''}${rub},${frac} ₽`;
}

/** Баланс словами: клиент читает «−640» как угодно, а «долг 640 ₽» однозначно. */
export function balanceLabel(kop: string | number | bigint): { text: string; kind: 'debt' | 'prepaid' | 'zero' } {
  const v = BigInt(kop);
  if (v < 0n) return { text: `Долг ${money(-v)}`, kind: 'debt' };
  if (v > 0n) return { text: `Аванс ${money(v)}`, kind: 'prepaid' };
  return { text: 'Ноль', kind: 'zero' };
}

export function phoneFmt(p: string | null | undefined): string {
  if (!p) return '';
  if (p.length === 11 && p.startsWith('7')) {
    return `+7 (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}`;
  }
  return p;
}

export function dateFmt(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function dateTimeFmt(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const STATUS_RU: Record<string, string> = {
  NEW: 'Новый',
  PLANNED: 'В маршруте',
  IN_ROUTE: 'В пути',
  DELIVERED: 'Доставлен',
  PARTIAL: 'Частично',
  FAILED: 'Не доставлен',
  CANCELED: 'Отменён',
  DRAFT: 'Черновик',
  ACTIVE: 'В работе',
  CLOSED: 'Закрыт',
  DONE: 'Доставлено',
};

export const PAYMENT_RU: Record<string, string> = {
  CASH: 'Наличные',
  CARD_COURIER: 'Карта у водителя',
  ONLINE: 'Онлайн',
  BANK_TRANSFER: 'По счёту',
  CREDIT: 'В долг',
  PREPAID: 'С аванса',
};
